import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, ArrowRight, Package, Wrench, Calculator, ClipboardList, BookOpen } from 'lucide-react';
import api from '../lib/api';

const MODULES = [
  { value: 'prezziario', label: 'Prezziario', icon: ClipboardList, desc: 'Codice, descrizione, prezzi fornitura/lavorazione' },
  { value: 'magazzino', label: 'Magazzino', icon: Package, desc: 'Articoli, quantita, fornitori, prezzi' },
  { value: 'attrezzature', label: 'Attrezzature', icon: Wrench, desc: 'Nome, matricola, marca, modello, stato' },
  { value: 'sal', label: 'Voci SAL', icon: Calculator, desc: 'Codice prezziario, quantita, data, cantiere' },
  { value: 'giornale_lavori', label: 'Giornale Lavori', icon: BookOpen, desc: 'Data, cantiere, ore, mq, personale, attivita' },
];

export default function DataImportPage() {
  const [module, setModule] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('skip');
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const loadSites = useCallback(async () => {
    try {
      const res = await api.get('/sites');
      setSites(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const handleModuleChange = (v) => {
    setModule(v);
    setFile(null);
    setPreview(null);
    setResult(null);
    if (v === 'sal' || v === 'giornale_lavori') loadSites();
  };

  const handleFile = async (f) => {
    if (!f || !module) return;
    setFile(f);
    setResult(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await api.post(`/data-import/preview/${module}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore lettura file');
      setPreview(null);
    } finally { setLoading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const handleExecute = async () => {
    if (!file || !module) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', mode);
      if (module === 'sal' || module === 'giornale_lavori') formData.append('site_id', selectedSite);
      const res = await api.post(`/data-import/execute/${module}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setPreview(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore importazione');
    } finally { setLoading(false); }
  };

  const handleTemplate = async (mod) => {
    try {
      const res = await api.get(`/data-import/templates/${mod}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `template_${mod}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Errore download template'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="data-import-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Importazione Dati</h1>
      </div>

      {/* Step 1: Module selection */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {MODULES.map(m => (
          <Card
            key={m.value}
            className={`cursor-pointer transition-all hover:shadow-md ${module === m.value ? 'ring-2 ring-[hsl(230,50%,20%)] border-[hsl(230,50%,20%)]' : 'border-slate-200'}`}
            onClick={() => handleModuleChange(m.value)}
            data-testid={`module-${m.value}`}
          >
            <CardContent className="p-4 text-center">
              <m.icon size={24} className={`mx-auto mb-2 ${module === m.value ? 'text-[hsl(230,50%,20%)]' : 'text-slate-400'}`} />
              <p className="text-sm font-semibold">{m.label}</p>
              <p className="text-xs text-slate-500 mt-1">{m.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {module && (
        <>
          {/* Template download */}
          <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
            <FileSpreadsheet size={18} className="text-slate-500 shrink-0" />
            <span className="text-sm text-slate-600 flex-1">Scarica il template Excel con le colonne corrette per l'importazione</span>
            <Button variant="outline" size="sm" onClick={() => handleTemplate(module)} data-testid="download-template-btn">
              <Download size={14} className="mr-1" /> Template {MODULES.find(m => m.value === module)?.label}
            </Button>
          </div>

          {/* Step 2: File Upload */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Carica File</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? 'border-[hsl(230,50%,20%)] bg-blue-50/50' : 'border-slate-300 hover:border-slate-400'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                data-testid="dropzone"
              >
                <Upload size={32} className="mx-auto text-slate-400 mb-3" />
                <p className="text-sm text-slate-600 mb-2">Trascina qui il file oppure</p>
                <label className="inline-block">
                  <input
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
                    data-testid="file-input"
                  />
                  <span className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[hsl(230,50%,20%)] text-white text-sm cursor-pointer hover:bg-[hsl(230,50%,25%)] transition-colors">
                    <Upload size={14} /> Seleziona file
                  </span>
                </label>
                <p className="text-xs text-slate-400 mt-2">Formati accettati: .xlsx, .xls, .csv</p>
                {file && <p className="text-sm text-slate-700 mt-3 font-medium">{file.name}</p>}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Step 3: Preview */}
      {loading && <div className="text-center py-8"><div className="animate-pulse text-slate-400">Elaborazione...</div></div>}

      {preview && !result && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base" data-testid="preview-title">Anteprima - {preview.total_rows} righe trovate</CardTitle>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">{preview.filename}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Column mapping info */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(preview.columns_mapped || {}).map(([field, col]) => (
                <div key={field} className="flex items-center gap-1 bg-green-50 rounded px-2 py-1 text-xs border border-green-200">
                  <CheckCircle2 size={12} className="text-green-600" />
                  <span className="text-green-700 font-medium">{field}</span>
                  <ArrowRight size={10} className="text-slate-400" />
                  <span className="text-slate-600">{col}</span>
                </div>
              ))}
              {(preview.missing_optional || []).map(field => (
                <div key={field} className="flex items-center gap-1 bg-amber-50 rounded px-2 py-1 text-xs border border-amber-200">
                  <AlertTriangle size={12} className="text-amber-600" />
                  <span className="text-amber-700">{field}</span>
                  <span className="text-slate-400">(non mappata)</span>
                </div>
              ))}
            </div>

            {/* Preview table */}
            <div className="overflow-x-auto border rounded max-h-[350px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 sticky top-0">
                    <TableHead className="text-xs w-10">#</TableHead>
                    {Object.keys(preview.columns_mapped || {}).map(field => (
                      <TableHead key={field} className="text-xs">{field}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(preview.preview || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs text-slate-400">{i + 1}</TableCell>
                      {Object.keys(preview.columns_mapped || {}).map(field => (
                        <TableCell key={field} className="text-xs max-w-[200px] truncate">{row[field] || '-'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Import options */}
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 pt-2 border-t">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Gestione duplicati</Label>
                <RadioGroup value={mode} onValueChange={setMode} className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="skip" id="skip" data-testid="mode-skip" />
                    <Label htmlFor="skip" className="text-sm cursor-pointer">Salta duplicati</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="update" id="update" data-testid="mode-update" />
                    <Label htmlFor="update" className="text-sm cursor-pointer">Aggiorna esistenti</Label>
                  </div>
                </RadioGroup>
              </div>

              {(module === 'sal' || module === 'giornale_lavori') && (
                <div className="space-y-1.5">
                  <Label className="text-sm">Cantiere destinazione</Label>
                  <Select value={selectedSite} onValueChange={setSelectedSite}>
                    <SelectTrigger className="w-52" data-testid="sal-site-select">
                      <SelectValue placeholder="Da colonna o seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">Automatico da colonna</SelectItem>
                      {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex-1" />
              <Button onClick={handleExecute} disabled={loading} className="gap-2" data-testid="execute-import-btn">
                <Upload size={16} /> Importa {preview.total_rows} righe
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Result */}
      {result && (
        <Card className="border-slate-200" data-testid="import-result">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 size={20} className="text-green-600" />
              Importazione Completata
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-slate-50 rounded-lg p-3 text-center border">
                <p className="text-2xl font-bold text-slate-700" data-testid="result-total">{result.total_rows}</p>
                <p className="text-xs text-slate-500">Righe Totali</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                <p className="text-2xl font-bold text-green-700" data-testid="result-imported">{result.imported}</p>
                <p className="text-xs text-green-600">Importate</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                <p className="text-2xl font-bold text-blue-700" data-testid="result-updated">{result.updated}</p>
                <p className="text-xs text-blue-600">Aggiornate</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
                <p className="text-2xl font-bold text-amber-700" data-testid="result-skipped">{result.skipped}</p>
                <p className="text-xs text-amber-600">Saltate</p>
              </div>
              <div className={`rounded-lg p-3 text-center border ${result.error_count > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
                <p className={`text-2xl font-bold ${result.error_count > 0 ? 'text-red-700' : 'text-slate-400'}`} data-testid="result-errors">{result.error_count}</p>
                <p className={`text-xs ${result.error_count > 0 ? 'text-red-600' : 'text-slate-500'}`}>Errori</p>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1"><XCircle size={14} /> Dettaglio errori:</p>
                {result.errors.map((err, i) => (
                  <p key={i} className="text-xs text-red-600 py-0.5">{err}</p>
                ))}
              </div>
            )}

            <Button variant="outline" onClick={() => { setResult(null); setFile(null); setPreview(null); }} data-testid="new-import-btn">
              Nuova Importazione
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
